import { computed, shallowRef, watch } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { assertNever } from "../lib/assertNever";
import {
  isJust,
  isNothing,
  just,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import { dispatchInto } from "../lib/tea";
import { syncFromCloud } from "../lib/cloudSync";
import {
  watchProjectTips,
  type StopWatchProjectTips,
} from "../lib/cloudRealtimeDesktop";
import { useCloudActivityBusy } from "../lib/cloudActivity";
import { getCloudSession } from "../lib/cloudAuth";
import { ensureCanonicalBackup, kickCloudSync } from "../lib/cloudBackup";
import { isTauri } from "../lib/runtime";
import { reloadEditorFromDisk } from "../lib/tauri/project";
import { applyPayload } from "./editorPayload";
import { useEditorHeadingsStore } from "./useEditorHeadingsStore";
import { useEditorSaveStore } from "./useEditorSaveStore";
import { useEditorSessionStore } from "./useEditorSessionStore";
import {
  initialEditorCloudModel,
  realtimeWatchKey,
  updateEditorCloud,
  type CloudSyncEnv,
  type EditorCloudCmd,
  type EditorCloudMsg,
  type RealtimeEnv,
} from "./useEditorCloudStore.update";

/**
 * Cloud sync for the open project (push, pull on open, realtime tips).
 *
 * TEA: pure Model/Msg/Cmd in `useEditorCloudStore.update.ts`. This shell reads
 * environment facts into Msgs and interprets Cmds (`syncFromCloud`,
 * `kickCloudSync`, `watchProjectTips`). Async outcomes come back as Msgs.
 */
export const useEditorCloudStore = defineStore("editorCloud", () => {
  const session = useEditorSessionStore();
  const headings = useEditorHeadingsStore();
  const { rootPath } = storeToRefs(session);
  const cloudSyncBusy = useCloudActivityBusy(rootPath);

  const model = shallowRef(initialEditorCloudModel(getCloudSession() !== null));
  const cloudSignedIn = computed(() => model.value.signedIn);

  /** Effect handles for the realtime watch (not model data). */
  let activeStop: Maybe<StopWatchProjectTips> = nothing;
  let startedStop: Maybe<StopWatchProjectTips> = nothing;

  function syncEnv(): CloudSyncEnv {
    return {
      tauri: isTauri(),
      rootPath: session.rootPath,
      signedIn: getCloudSession() !== null,
      busy: cloudSyncBusy.value,
    };
  }

  function cloudProjectIdKey(): string {
    if (isNothing(session.projectConfig)) return "";
    const id = toMaybe(session.projectConfig.value.cloudProjectId);
    if (isNothing(id)) return "";
    return id.value.trim();
  }

  function realtimeEnv(): RealtimeEnv {
    return {
      tauri: isTauri(),
      kind: session.kind,
      rootPath: session.rootPath,
      signedIn: getCloudSession() !== null,
      cloudProjectId: cloudProjectIdKey(),
    };
  }

  function stopActiveWatch(): void {
    if (isJust(activeStop)) activeStop.value();
    activeStop = nothing;
  }

  async function reloadAfterCloudUpdate(): Promise<void> {
    if (isNothing(session.rootPath)) return;
    const payload = await reloadEditorFromDisk(session.rootPath.value);
    applyPayload(payload);
    await headings.refreshEntityOccurrences();
  }

  async function runCmd(cmd: EditorCloudCmd): Promise<void> {
    switch (cmd.kind) {
      case "kickSync":
        try {
          await kickCloudSync(cmd.rootPath);
        } catch (error) {
          console.warn(
            cmd.source === "immediate"
              ? "[cloudBackup] immediate sync failed"
              : "[cloudBackup] blur sync failed",
            error,
          );
        }
        return;
      case "flushSaveThenPush": {
        const save = useEditorSaveStore();
        for (let i = 0; i < 40 && save.saving; i++) {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
        await save.flushSave();
        await pushImmediateCloudSync();
        return;
      }
      case "pullFromCloud": {
        let result: Extract<EditorCloudMsg, { type: "PullFinished" }>["result"];
        try {
          const synced = await syncFromCloud(cmd.rootPath, "open");
          result = { kind: "synced", status: synced.status };
        } catch (error) {
          console.warn("[cloudSync] open pull failed", error);
          result = { kind: "failed" };
        }
        await send({ type: "PullFinished", rootPath: cmd.rootPath, result });
        return;
      }
      case "reloadAfterOpenPull":
        try {
          await reloadAfterCloudUpdate();
        } catch (error) {
          console.warn("[cloudSync] open pull failed", error);
        }
        await send({ type: "OpenPullReloaded", rootPath: cmd.rootPath });
        return;
      case "reloadFromDisk":
        await reloadAfterCloudUpdate();
        return;
      case "ensureBackup":
        // Tips may already match while the stored pack is garbage. Check and
        // silently replace with a thick pack when needed.
        try {
          await ensureCanonicalBackup(cmd.rootPath);
        } catch (error) {
          console.warn("[cloudBackup] open backup check failed", error);
        }
        return;
      case "startWatch": {
        const stop = await watchProjectTips({
          rootPath: cmd.rootPath,
          onSynced: async (result) => {
            await send({ type: "TipSynced", status: result.status });
          },
        });
        startedStop = just(stop);
        await send({
          type: "WatchStarted",
          startId: cmd.startId,
          key: cmd.key,
        });
        return;
      }
      case "stopWatch":
        stopActiveWatch();
        return;
      case "adoptStartedWatch":
        activeStop = startedStop;
        startedStop = nothing;
        return;
      case "discardStartedWatch":
        if (isJust(startedStop)) startedStop.value();
        startedStop = nothing;
        return;
      case "requestRealtime":
        void startCloudRealtimeWatch();
        return;
      default:
        assertNever(cmd);
    }
  }

  function send(msg: EditorCloudMsg): Promise<void> {
    return dispatchInto(model, msg, updateEditorCloud, runCmd);
  }

  async function pushImmediateCloudSync(): Promise<void> {
    await send({ type: "PushImmediateRequested", env: syncEnv() });
  }

  async function onRequestSync(): Promise<void> {
    await send({ type: "ManualSyncRequested", env: syncEnv() });
  }

  async function onBlurSync(): Promise<void> {
    await send({ type: "BlurSyncRequested", env: syncEnv() });
  }

  async function pullCloudOnOpen(): Promise<void> {
    await send({ type: "PullOnOpenRequested", env: syncEnv() });
  }

  async function startCloudRealtimeWatch(): Promise<void> {
    await send({ type: "RealtimeRequested", env: realtimeEnv() });
  }

  function markRealtimeReady(): void {
    void send({ type: "RealtimeReady" });
  }

  watch(
    () =>
      realtimeWatchKey({
        ...realtimeEnv(),
        signedIn: model.value.signedIn,
      }),
    () => {
      void startCloudRealtimeWatch();
    },
  );

  function dispose(): void {
    void send({ type: "Disposed" });
  }

  return {
    cloudSyncBusy,
    cloudSignedIn,
    pushImmediateCloudSync,
    onRequestSync,
    onBlurSync,
    pullCloudOnOpen,
    reloadAfterCloudUpdate,
    markRealtimeReady,
    dispose,
  };
});
